import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "common/components/ui/Tabs";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import { getProfileSubtitle } from "common/helpers/utils";
import useDebounce from "common/hooks/useDebounce";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LinkedInProfile } from "common/types";

import useBlacklist from "../../../datahooks/useBlacklist";
import useBlacklistMutations from "../../../datahooks/useBlacklistMutations";
import useProfileSearch from "../../../datahooks/useProfileSearch";
import BlacklistItem from "../BlacklistItem";
import CsvUpload from "../CsvUpload";
import AddToBlacklistSearch from "./AddToBlacklistSearch";

import EmptyStateImage from "assets/images/empty-placeholders/blacklist.png";

export default function BlacklistAccounts({
  onClose,
}: {
  onClose: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { addToBlacklist, isAddingToBlacklist } = useBlacklistMutations();
  const [selectedTab, setSelectedTab] = useState("0");
  const [failedUrls, setFailedUrls] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState<LinkedInProfile[]>(
    [],
  );
  const [keywords, setKeywords] = useState("");
  const [debouncedKeywords] = useDebounce(keywords, 600);
  const { profiles, isLoadingProfiles } = useProfileSearch(debouncedKeywords);
  const { blacklist } = useBlacklist("accounts");
  const { accounts } = useSelectedWorkspaceContext();

  function onBack() {
    setSelectedAccounts([]);
    setFailedUrls(null);
    setSelectedTab("1");
  }
  function onGetProfiles(profiles: LinkedInProfile[], failed: string[]) {
    setSelectedAccounts(profiles);
    setFailedUrls(
      failed.map((identifier) => `https://www.linkedin.com/in/${identifier}`),
    );
    setSelectedTab("0");
  }
  function addAccounts() {
    addToBlacklist({ accounts: selectedAccounts }).then(() => {
      setSelectedAccounts([]);
      onClose();
    });
  }
  function removeAccount(accountId: string) {
    const filteredAccounts = selectedAccounts.filter(
      ({ id }) => id !== accountId,
    );
    setSelectedAccounts(filteredAccounts);
    if (!filteredAccounts.length) {
      setFailedUrls(null);
    }
  }

  const searchResults = profiles
    ?.filter(
      (profile) =>
        !blacklist.find(({ id }) => profile.id === id) &&
        !selectedAccounts.find(({ id }) => profile.id === id) &&
        !accounts.find(({ id }) => profile.id === id),
    )
    .map((profile) => {
      const { id, full_name, picture_url } = profile;
      return {
        id,
        pictureUrl: picture_url,
        title: full_name,
        subtitle: getProfileSubtitle(profile),
        originalObject: profile,
      };
    });

  return (
    <>
      <div className="flex flex-1 flex-col overflow-hidden border-t-black-200 lg:border-t lg:px-6 lg:py-4">
        <Tabs
          value={selectedTab}
          onValueChange={setSelectedTab}
          className="-m-0.5 flex flex-1 flex-col overflow-hidden p-0.5"
        >
          <TabsList className="mb-4 pb-2">
            <TabsTrigger value="0" data-cy="tab-search">
              {failedUrls ? `Success (${selectedAccounts.length})` : "Search"}
            </TabsTrigger>
            {(!failedUrls || !!failedUrls.length) && (
              <TabsTrigger value="1" data-cy="tab-csv">
                {failedUrls
                  ? `Failed (${failedUrls.length})`
                  : "Upload .csv file"}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent
            value="0"
            className="-m-0.5 flex flex-col overflow-y-hidden p-0.5 data-[state=active]:flex-1"
          >
            {!failedUrls && (
              <AddToBlacklistSearch
                isLoading={isLoadingProfiles}
                searchText={keywords}
                setSearchText={setKeywords}
                searchResults={searchResults}
                onSelectResult={(account) => {
                  setSelectedAccounts([...selectedAccounts, account]);
                }}
                type="accounts"
              />
            )}
            {selectedAccounts.length ? (
              <div className="grid grid-cols-1 gap-4 overflow-y-auto max-lg:mb-4 lg:grid-cols-2">
                {selectedAccounts.map((profile) => {
                  const { id, picture_url, full_name } = profile;
                  return (
                    <BlacklistItem
                      key={id}
                      pictureUrl={picture_url}
                      title={full_name}
                      subtitle={getProfileSubtitle(profile)}
                      onRemove={() => removeAccount(id)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="mx-auto flex max-w-72 grow flex-col items-center justify-center gap-4 max-lg:my-4 lg:max-w-96">
                <img alt="" src={EmptyStateImage} width={200} />
                <h5 className="text-center text-2xl-bold">
                  No accounts to blacklist
                </h5>
                <p className="-mt-2 text-center text-black-500">
                  Search accounts you want to add to your blacklist. Once you
                  are satisfied with your selection, click &quot;Add to
                  blacklist&quot; to blacklist them
                </p>
              </div>
            )}
          </TabsContent>
          <TabsContent
            value="1"
            className="flex flex-col data-[state=active]:flex-1"
          >
            <CsvUpload failedUrls={failedUrls} onGetProfiles={onGetProfiles} />
          </TabsContent>
        </Tabs>
      </div>
      {(selectedTab === "0" || !!selectedAccounts.length || !!failedUrls) && (
        <div className="flex border-t border-t-black-200 px-6 pt-4 lg:py-4">
          {failedUrls && (
            <Button
              onClick={onBack}
              size={isDesktop ? "md" : "lg"}
              variant="secondary-black"
              data-cy="csv-next-button"
            >
              Back
            </Button>
          )}
          <Button
            className="ml-auto"
            size={isDesktop ? "md" : "lg"}
            disabled={!selectedAccounts.length}
            onClick={addAccounts}
            isLoading={isAddingToBlacklist}
            data-cy="add-to-blacklist-button"
          >
            Add to blacklist
          </Button>
        </div>
      )}
    </>
  );
}
